import { useCallback, useState } from 'react';

import { useBreakPoints, useTranslate } from '@src/core/hooks';
import { resolveTransactableEntity, type Transactable } from '@src/domains/transactables';
import { resolvePixExpiration, resolveTimeRemaining } from '@src/domains/transactions';

const MS_IN_SECOND = 1000;

export const usePixPaymentCard = (transactable: Transactable) => {
  const { i18n } = useTranslate();
  const { currentBreakpoint, sm } = useBreakPoints();

  const entity = resolveTransactableEntity(transactable);
  const lastTransaction = entity?.lastTransaction;

  const [ranOut, setRanOut] = useState(false);

  const expiration = resolvePixExpiration(lastTransaction);
  const remainingMs = resolveTimeRemaining(expiration, new Date());

  // The uni countdown ticks a local counter, so a throttled tab makes it report the deadline late.
  // Reading the clock here keeps the card honest: every render — and the pix polling guarantees
  // them — re-checks the real deadline, and `ranOut` covers the stretch in between.
  const isExpired = ranOut || (remainingMs != null && remainingMs <= 0);

  const compact = currentBreakpoint < sm;

  return {
    isExpired,
    pix: lastTransaction?.pix,
    secondsRemaining: Math.ceil((remainingMs ?? 0) / MS_IN_SECOND),
    timerSize: compact ? ('sm' as const) : ('md' as const),
    onTimerRanOut: useCallback(() => setRanOut(true), []),
    // The retry rebuilds a cart out of an order, so it is only offered when the pending pix is the
    // order's own. A sale change resolves ahead of the order and has nothing to rebuild from.
    orderId: entity && entity === transactable.order ? transactable.order.id : undefined,
    changeMethodLabel:
      isExpired && compact
        ? i18n('checkout_payment_pix_change_method_compact')
        : i18n('checkout_payment_pix_change_method'),
  };
};
